/**
 * HVAC Templates Page
 *
 * Page component for browsing and applying HVAC system templates.
 * Part of Epic 2.2 - HVAC Templates Library
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TemplatePanel, useTemplateStore } from '@/templates';
import { useIDFStore } from '@/stores/idfStore';
import { FileRequiredEmptyState } from './FileRequiredEmptyState';

/**
 * Success notification component
 */
function SuccessNotification({ onDismiss, onViewSystem }: { onDismiss: () => void; onViewSystem: () => void }) {
  const lastResult = useTemplateStore((s) => s.lastResult);

  if (!lastResult?.success) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 right-2 left-2 md:left-auto md:right-4 bg-background border rounded-lg shadow-lg p-4 max-w-full md:max-w-sm animate-in slide-in-from-bottom-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm">Template Applied Successfully</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Created {lastResult.nodeIds.length} components and {lastResult.edgeIds.length} connections
            {lastResult.plantLoopIds.length > 0 && ` with ${lastResult.plantLoopIds.length} plant loop(s)`}
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={onViewSystem}>
              View System
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Empty state when no IDF is loaded
 */
function NoIDFState() {
  const navigate = useNavigate();

  return (
    <FileRequiredEmptyState
      reason="no-file"
      title="No Model Loaded"
      message="Load an IDF file first to apply HVAC templates. Templates need zone information from your building model."
      action={{
        label: 'Go to Editor',
        onClick: () => navigate('/'),
      }}
    />
  );
}

/**
 * Templates Page Component
 */
export function TemplatesPage() {
  const navigate = useNavigate();
  const idfDocument = useIDFStore((s) => s.document);
  const lastResult = useTemplateStore((s) => s.lastResult);
  const setLastResult = useTemplateStore((s) => s.setLastResult);
  const [showNotification, setShowNotification] = useState(false);

  // Show notification when template is applied
  useEffect(() => {
    if (lastResult?.success) {
      setShowNotification(true);
    }
  }, [lastResult]);

  const handleDismissNotification = () => {
    setShowNotification(false);
    setLastResult(null);
  };

  const handleViewSystem = () => {
    setShowNotification(false);
    setLastResult(null);
    navigate('/hvac');
  };

  // Show empty state if no IDF is loaded
  if (!idfDocument) {
    return <NoIDFState />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold">HVAC Templates</h1>
            <span className="text-sm text-muted-foreground">
              Select a template to quickly create an HVAC system
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <TemplatePanel />
      </div>

      {/* Success Notification */}
      {showNotification && (
        <SuccessNotification
          onDismiss={handleDismissNotification}
          onViewSystem={handleViewSystem}
        />
      )}
    </div>
  );
}

export default TemplatesPage;
